/**
 * Index strip — NIFTY 50 / BANK NIFTY / … under the header, like the broker apps.
 *
 * Values come from `GET /market/indices` (cached or a REST snapshot) and then
 * stream over the shared market websocket for the indices Kotak streams
 * (NIFTY, BANKNIFTY, FINNIFTY, MIDCPNIFTY, NIFTY NEXT 50, SENSEX, BANKEX).
 * Sector indices have no feed token and refresh with the 30s poll. The gear
 * lets the user pick up to `max_selected` indices; the choice is stored
 * per user on the backend.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, RotateCcw, Settings2 } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { useMarketStore } from '@/stores/market-store';
import type { TickData } from '@/stores/market-store';
import { useMarketWebSocket } from '@/hooks/use-websocket';
import { Dropdown } from '@/components/ui/dropdown';
import { Button } from '@/components/ui';
import { formatPctFixed, formatPrice, formatSignedPrice, signClass, toNum } from '@/lib/format';
import { cn } from '@/lib/cn';

interface IndexDef {
  key: string;
  name: string;
  short_name: string;
  exchange_segment: string;
  instrument_token: string | null;
  streams: boolean;
}

/** Quote rows use the websocket's short keys (`QuoteResponse` serialises by alias). */
interface IndexQuote {
  ltp?: string;
  c?: string;
  chg?: string;
  chgp?: string;
  t?: string | null;
}

interface IndexStripPayload {
  catalog: IndexDef[];
  selected: string[];
  max_selected: number;
  values: Record<string, IndexQuote>;
}

const POLL_MS = 30_000;
const DEFAULT_MAX = 8;

interface Shown {
  def: IndexDef;
  ltp: number | null;
  change: number | null;
  changePct: number | null;
}

function fromTick(tick: TickData | undefined): Pick<Shown, 'ltp' | 'change' | 'changePct'> | null {
  if (!tick) return null;
  const ltp = toNum(tick.ltp, 0);
  if (ltp <= 0) return null;
  return { ltp, change: toNum(tick.change, 0), changePct: toNum(tick.change_percent, 0) };
}

function fromQuote(quote: IndexQuote | undefined): Pick<Shown, 'ltp' | 'change' | 'changePct'> | null {
  if (!quote) return null;
  const ltp = toNum(quote.ltp, 0);
  if (ltp <= 0) return null;
  return { ltp, change: toNum(quote.chg, 0), changePct: toNum(quote.chgp, 0) };
}

export function IndexStrip({ className }: { className?: string }) {
  const [catalog, setCatalog] = useState<IndexDef[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [maxSelected, setMaxSelected] = useState(DEFAULT_MAX);
  const [restValues, setRestValues] = useState<Record<string, IndexQuote>>({});
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const ticks = useMarketStore((s) => s.ticks);
  const updateTick = useMarketStore((s) => s.updateTick);
  const { isConnected, subscribe, unsubscribe } = useMarketWebSocket();

  const applyPayload = useCallback(
    (data: IndexStripPayload) => {
      setCatalog(data.catalog);
      setSelected(data.selected);
      setDraft(data.selected);
      setMaxSelected(data.max_selected || DEFAULT_MAX);
      setRestValues(data.values || {});
      // Seed the shared store so streaming indices show a value before the first tick.
      for (const def of data.catalog) {
        const quote = data.values?.[def.key];
        if (!def.instrument_token || !quote || toNum(quote.ltp, 0) <= 0) continue;
        if (ticks[def.instrument_token] && toNum(ticks[def.instrument_token].ltp, 0) > 0) continue;
        updateTick(def.instrument_token, {
          instrument_token: def.instrument_token,
          trading_symbol: def.short_name,
          exchange_segment: def.exchange_segment,
          ltp: quote.ltp ?? '0',
          close: quote.c ?? '0',
          change: quote.chg ?? '0',
          change_percent: quote.chgp ?? '0',
          updated_at: quote.t ?? undefined,
        });
      }
    },
    [ticks, updateTick]
  );

  // Initial load + poll (REST-only indices have no feed; streaming ones come from the cache).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data } = await api.get<IndexStripPayload>('/market/indices');
        if (!cancelled) applyPayload(data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // applyPayload changes with every tick; the poll must not restart each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shownDefs = useMemo(
    () => selected.map((key) => catalog.find((d) => d.key === key)).filter((d): d is IndexDef => Boolean(d)),
    [selected, catalog]
  );
  const streamingTokens = useMemo(
    () => shownDefs.map((d) => d.instrument_token).filter((t): t is string => Boolean(t)),
    [shownDefs]
  );

  // Keep the websocket subscription in step with the selection.
  useEffect(() => {
    if (!isConnected || streamingTokens.length === 0) return;
    subscribe(streamingTokens);
    return () => unsubscribe(streamingTokens);
  }, [isConnected, streamingTokens, subscribe, unsubscribe]);

  const rows: Shown[] = shownDefs.map((def) => {
    const live = def.instrument_token ? fromTick(ticks[def.instrument_token]) : null;
    const snap = fromQuote(restValues[def.key]);
    const value = live ?? snap;
    return { def, ltp: value?.ltp ?? null, change: value?.change ?? null, changePct: value?.changePct ?? null };
  });

  const toggleDraft = (key: string) => {
    setDraft((current) => {
      if (current.includes(key)) return current.length > 1 ? current.filter((k) => k !== key) : current;
      if (current.length >= maxSelected) return current;
      return [...current, key];
    });
  };

  const save = async (close: () => void) => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put<IndexStripPayload>('/market/indices', { keys: draft });
      applyPayload(data);
      close();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const draftChanged = draft.join(',') !== selected.join(',');

  return (
    <div
      className={cn(
        'flex h-9 shrink-0 items-center gap-2 border-b border-line bg-canvas px-4 lg:px-6',
        className
      )}
      aria-label="Market indices"
    >
      <ul className="scrollbar-none flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {rows.length === 0 && (
          <li className="text-xs text-fg-faint">{error ? `Indices unavailable — ${error}` : 'Loading indices…'}</li>
        )}
        {rows.map(({ def, ltp, change, changePct }) => (
          <li
            key={def.key}
            // Fixed width + right-aligned tabular figures: the chip never grows or shrinks as prices tick,
            // so the eye can rest on one column per index.
            // Columns sized for the widest realistic values: name "MIDCPNIFTY", price "76,019.22",
            // change "-1,234.56 (-1.62%)". 5.5 + 5 + 6.5rem + two 0.5rem gaps + 1rem padding = 19rem.
            className="grid w-[19rem] shrink-0 grid-cols-[5.5rem_5rem_6.5rem] items-baseline gap-x-2 rounded-md px-2 py-1 text-xs"
            title={`${def.name} · ${def.streams ? 'streams live' : 'refreshes every 30s'}`}
          >
            <span className="truncate font-medium text-fg-muted">{def.short_name}</span>
            <span className="text-right font-tabular whitespace-nowrap text-fg">{ltp === null ? '—' : formatPrice(ltp)}</span>
            <span
              className={cn(
                'text-right font-tabular whitespace-nowrap',
                change === null ? 'text-fg-faint' : signClass(change)
              )}
            >
              {change === null || changePct === null ? '' : `${formatSignedPrice(change)} (${formatPctFixed(changePct)})`}
            </span>
          </li>
        ))}
      </ul>

      <Dropdown
        renderTrigger={({ open, triggerProps }) => (
          <button
            aria-label="Choose indices"
            title="Choose which indices to show"
            className={cn(
              'rounded-md p-1.5 transition-colors hover:bg-surface-2 hover:text-fg',
              open ? 'bg-surface-2 text-fg' : 'text-fg-muted'
            )}
            {...triggerProps}
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
        panelClassName="w-72 p-0"
      >
        {(close) => (
          <div className="flex flex-col">
            <div className="border-b border-line px-3 py-2">
              <p className="text-sm font-medium text-fg">Indices on the strip</p>
              <p className="text-xs text-fg-subtle">
                Pick up to {maxSelected}. {draft.length}/{maxSelected} selected.
              </p>
            </div>
            <ul className="max-h-72 overflow-y-auto p-1" role="group" aria-label="Available indices">
              {catalog.map((def) => {
                const checked = draft.includes(def.key);
                const disabled = !checked && draft.length >= maxSelected;
                return (
                  <li key={def.key}>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={checked}
                      disabled={disabled}
                      onClick={() => toggleDraft(def.key)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                        disabled ? 'cursor-not-allowed text-fg-faint' : 'text-fg-muted hover:bg-surface-2 hover:text-fg'
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          checked ? 'border-brand bg-brand text-white' : 'border-line-strong bg-surface-2'
                        )}
                        aria-hidden="true"
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{def.name}</span>
                      <span className="text-[10px] tracking-wider text-fg-faint uppercase">
                        {def.streams ? 'live' : '30s'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {error && <p className="px-3 pb-1 text-xs text-danger">{error}</p>}
            <div className="flex items-center justify-between gap-2 border-t border-line px-2 py-2">
              <Button
                variant="ghost"
                size="sm"
                icon={RotateCcw}
                onClick={() => setDraft(selected)}
                disabled={!draftChanged}
              >
                Reset
              </Button>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => { setDraft(selected); close(); }}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" loading={saving} disabled={!draftChanged} onClick={() => save(close)}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </Dropdown>
    </div>
  );
}
